import React, {Dispatch, FC, SetStateAction} from 'react'
import SearchableLookupField from './SearchableLookupField'
import type {CurrencyOption} from '../../Management/core/_requests'
import {
  AccountingAccount,
  CompanyGroupAdmin,
  Equipment,
  InventoryCategory,
  InventoryItemHead,
  InventoryItemSubHead,
  InventoryItemSubCategory,
  Part,
  Port,
  SubAccount,
  SubComponent,
  UnitOfMeasure,
  Vessel,
  VesselInventoryProjection,
  Vendor,
} from '../core/_models'
import {
  DirectPoCategoryKind,
  DirectPoFormDraft,
  DirectPoLineDraft,
} from './directPurchaseOrderForm'
import {formatExchangeRateDisplay} from '../core/currencyConversion'

type LoadStatus = 'idle' | 'loading' | 'loaded' | 'empty' | 'error'

type ProcurementContextSectionProps = {
  inventoryScopeAll: boolean
  selectedCompanyId: number | null
  selectedCompanyName: string
  companies: CompanyGroupAdmin[]
  vessels: Vessel[]
  vendors: Vendor[]
  ports: Port[]
  categories: InventoryCategory[]
  form: DirectPoFormDraft
  derivedRequestFor: string
  errors: Record<string, string>
  onCompanyChange: (companyIdRaw: string) => void | Promise<void>
  onVesselChange: (vesselId: string) => void
  onVendorChange: (vendorId: string) => void
  onRequestedPortChange: (requestedPortId: string) => void
  onDeliveryPortChange: (deliveryPortId: string) => void
  onCategoryChange: (categoryId: string) => void
}

export const ProcurementContextSection: FC<ProcurementContextSectionProps> = ({
  inventoryScopeAll,
  selectedCompanyId,
  selectedCompanyName,
  companies,
  vessels,
  vendors,
  ports,
  categories,
  form,
  derivedRequestFor,
  errors,
  onCompanyChange,
  onVesselChange,
  onVendorChange,
  onRequestedPortChange,
  onDeliveryPortChange,
  onCategoryChange,
}) => (
  <section className='mb-6'>
    <h6 className='fw-bold mb-3'>1. Procurement Context</h6>
    <div className='row g-4'>
      {inventoryScopeAll && (
        <div className='col-md-4'>
          <label className='form-label required'>Company</label>
          <select
            aria-label='Direct PO Company'
            className='form-select'
            value={selectedCompanyId ?? ''}
            onChange={(e) => void onCompanyChange(e.target.value)}
          >
            <option value=''>Select company</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name || company.username}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className='col-md-4'>
        <label className='form-label required'>Vessel</label>
        <select
          aria-label='Direct PO Vessel'
          className={`form-select ${errors.vesselId ? 'is-invalid' : ''}`}
          value={form.vesselId}
          onChange={(e) => onVesselChange(e.target.value)}
          disabled={inventoryScopeAll ? !selectedCompanyId : vessels.length === 0}
        >
          <option value=''>Select vessel</option>
          {vessels.map((vessel) => (
            <option key={vessel.id} value={vessel.id}>
              {vessel.fleet_name} {vessel.vesselType ? `(${vessel.vesselType})` : ''}
            </option>
          ))}
        </select>
        <div className='form-text'>
          {inventoryScopeAll && !selectedCompanyId
            ? 'Select company first'
            : selectedCompanyName
            ? `Showing vessels for ${selectedCompanyName}`
            : 'Showing vessels in your scope'}
        </div>
      </div>
      <div className='col-md-4'>
        <label className='form-label required'>Vendor</label>
        <select
          aria-label='Direct PO Vendor'
          className={`form-select ${errors.vendorId ? 'is-invalid' : ''}`}
          value={form.vendorId}
          onChange={(e) => onVendorChange(e.target.value)}
        >
          <option value=''>Select vendor</option>
          {vendors.map((vendor) => (
            <option key={vendor.id} value={vendor.id}>
              {vendor.name}
            </option>
          ))}
        </select>
      </div>
      <div className='col-md-4'>
        <label className='form-label required'>Requested Port</label>
        <select
          aria-label='Direct PO Requested Port'
          className={`form-select ${errors.requestedPortId ? 'is-invalid' : ''}`}
          value={form.requestedPortId}
          onChange={(e) => onRequestedPortChange(e.target.value)}
        >
          <option value=''>Select port</option>
          {ports.map((port) => (
            <option key={port.id} value={port.id}>
              {port.name}
            </option>
          ))}
        </select>
      </div>
      <div className='col-md-4'>
        <label className='form-label'>Delivery Port</label>
        <select
          aria-label='Direct PO Delivery Port'
          className='form-select'
          value={form.deliveryPortId}
          onChange={(e) => onDeliveryPortChange(e.target.value)}
        >
          <option value=''>Select delivery port</option>
          {ports.map((port) => (
            <option key={port.id} value={port.id}>
              {port.name}
            </option>
          ))}
        </select>
      </div>
      <div className='col-md-4'>
        <SearchableLookupField
          label='Category'
          required
          value={form.categoryId}
          options={categories.map((category) => ({
            id: category.id,
            label: category.name,
          }))}
          onChange={onCategoryChange}
          placeholder={
            inventoryScopeAll && !selectedCompanyId
              ? 'Select company first'
              : !form.vesselId
              ? 'Select vessel first'
              : 'Type to search categories'
          }
          listId='direct-po-category-suggestions'
          error={errors.categoryId}
          wrapperClassName='w-100'
        />
        {inventoryScopeAll && !selectedCompanyId && (
          <div className='form-text'>Category scope follows the selected company.</div>
        )}
      </div>
      <div className='col-md-4'>
        <label className='form-label'>Request For</label>
        <input
          aria-label='Direct PO Request For'
          className='form-control'
          value={derivedRequestFor}
          readOnly
        />
      </div>
    </div>
  </section>
)

type BasicDetailsSectionProps = {
  today: string
  form: DirectPoFormDraft
  errors: Record<string, string>
  onFormChange: (patch: Partial<DirectPoFormDraft>) => void
}

export const BasicDetailsSection: FC<BasicDetailsSectionProps> = ({
  today,
  form,
  errors,
  onFormChange,
}) => (
  <section className='mb-6'>
    <h6 className='fw-bold mb-3'>2. Basic Details</h6>
    <div className='row g-4'>
      <div className='col-md-6'>
        <label className='form-label'>Requisition / Direct PO Title</label>
        <input
          aria-label='Direct PO Title'
          className='form-control'
          value={form.title}
          onChange={(e) => onFormChange({title: e.target.value})}
        />
      </div>
      <div className='col-md-6'>
        <label className='form-label'>Description</label>
        <input
          aria-label='Direct PO Description'
          className='form-control'
          value={form.description}
          onChange={(e) => onFormChange({description: e.target.value})}
        />
      </div>
      <div className='col-md-6'>
        <label className='form-label'>Requisition Description</label>
        <input
          aria-label='Direct PO Requisition Description'
          className='form-control'
          value={form.requisitionDescription}
          onChange={(e) => onFormChange({requisitionDescription: e.target.value})}
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Urgency</label>
        <select
          aria-label='Direct PO Urgency'
          className='form-select'
          value={form.requisitionType}
          onChange={(e) => onFormChange({requisitionType: e.target.value})}
        >
          <option value='HIGH'>HIGH</option>
          <option value='MEDIUM'>MEDIUM</option>
          <option value='LOW'>LOW</option>
        </select>
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Required Delivery Date</label>
        <input
          aria-label='Direct PO Required Delivery Date'
          type='date'
          min={today}
          className={`form-control ${errors.requiredDeliveryDate ? 'is-invalid' : ''}`}
          value={form.requiredDeliveryDate}
          onChange={(e) => onFormChange({requiredDeliveryDate: e.target.value})}
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>PO Date</label>
        <input
          aria-label='Direct PO Date'
          type='date'
          className='form-control'
          value={form.poDate}
          onChange={(e) => onFormChange({poDate: e.target.value})}
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Delivery Date</label>
        <input
          aria-label='Direct PO Delivery Date'
          type='date'
          className='form-control'
          value={form.deliveryDate}
          onChange={(e) => onFormChange({deliveryDate: e.target.value})}
        />
      </div>
    </div>
  </section>
)

type GeneralStoresLineFieldsProps = {
  line: DirectPoLineDraft
  lineIndex: number
  error?: string
  storeOptions: VesselInventoryProjection[]
  subCategories: InventoryItemSubCategory[]
  onLineChange: (lineIndex: number, patch: Partial<DirectPoLineDraft>) => void
  onLoadStoreOptions: (lineIndex: number, search: string) => Promise<void> | void
}

export const GeneralStoresLineFields: FC<GeneralStoresLineFieldsProps> = ({
  line,
  lineIndex,
  error,
  storeOptions,
  subCategories,
  onLineChange,
  onLoadStoreOptions,
}) => (
  <>
    <div className='col-md-4'>
      <label className='form-label required'>Sub Category</label>
      <select
        aria-label={`Direct PO Store Sub Category ${lineIndex + 1}`}
        className='form-select'
        value={line.inventoryItemSubCategoryId ?? ''}
        onChange={(e) => {
          const value = e.target.value ? Number(e.target.value) : null
          onLineChange(lineIndex, {
            inventoryItemSubCategoryId: value,
            itemSearchText: '',
            manualItemName: '',
            inventoryItemId: null,
            masterInventoryItemId: null,
            unitOfMeasurement: '',
            robDisplay: '',
          })
          void onLoadStoreOptions(lineIndex, '')
        }}
      >
        <option value=''>All sub categories</option>
        {subCategories.map((subCategory) => (
          <option key={subCategory.id} value={subCategory.id}>{subCategory.name}</option>
        ))}
      </select>
    </div>
    <SearchableLookupField
      label='IMPA / General Stores Item'
      value={
        line.masterInventoryItemId
          ? `master:${line.masterInventoryItemId}`
          : line.inventoryItemId
          ? `inventory:${line.inventoryItemId}`
          : ''
      }
      searchText={line.itemSearchText}
      displayValue={line.itemSearchText}
      options={storeOptions.map((item) => ({
        id: item.masterItemId ? `master:${item.masterItemId}` : `inventory:${item.inventoryItemId}`,
        label: `${item.itemCode || ''} ${item.itemName}`.trim(),
      }))}
      onChange={(value) => {
        const selected = storeOptions.find((item) => {
          const id = item.masterItemId ? `master:${item.masterItemId}` : `inventory:${item.inventoryItemId}`
          return id === value
        })
        if (!selected) return
        onLineChange(lineIndex, {
          itemSearchText: `${selected.itemCode || ''} ${selected.itemName}`.trim(),
          inventoryItemId: selected.masterItemId ? null : selected.inventoryItemId || null,
          masterInventoryItemId: selected.masterItemId || null,
          inventoryItemSubCategoryId: selected.inventoryItemSubCategoryId || null,
          manualItemName: selected.itemName,
          unitOfMeasurement: selected.unitOfMeasurement || '',
          robDisplay: selected.currentQty != null ? String(selected.currentQty) : '',
        })
      }}
      onSearchTextChange={(value) => {
        onLineChange(lineIndex, {itemSearchText: value, manualItemName: value})
        void onLoadStoreOptions(lineIndex, value)
      }}
      placeholder='Search IMPA / general items'
      listId={`direct-po-store-line-${lineIndex}`}
      wrapperClassName='col-md-6'
      error={error}
    />
    <div className='col-md-2'>
      <label className='form-label'>ROB</label>
      <input
        aria-label={`Direct PO ROB ${lineIndex + 1}`}
        className='form-control'
        value={line.robDisplay}
        readOnly
      />
    </div>
    <div className='col-md-4'>
      <label className='form-label'>Manual Item Name</label>
      <input
        aria-label={`Direct PO Manual Item Name ${lineIndex + 1}`}
        className='form-control'
        value={line.manualItemName}
        onChange={(e) => onLineChange(lineIndex, {manualItemName: e.target.value})}
      />
    </div>
  </>
)

type TechnicalSpareLineFieldsProps = {
  vesselId: string
  line: DirectPoLineDraft
  lineIndex: number
  equipmentOptions: Equipment[]
  equipmentStatus: string
  equipmentMessage: string | null
  subcomponents: SubComponent[]
  subcomponentStatus: LoadStatus
  subcomponentMessage: string | null
  parts: Part[]
  partStatus: LoadStatus
  partMessage: string | null
  errors: Record<string, string>
  onLineChange: (lineIndex: number, patch: Partial<DirectPoLineDraft>) => void
  onLoadSubcomponents: (lineIndex: number, equipmentId: string) => Promise<void> | void
  onLoadParts: (lineIndex: number, subcomponentId: string) => Promise<void> | void
}

export const TechnicalSpareLineFields: FC<TechnicalSpareLineFieldsProps> = ({
  vesselId,
  line,
  lineIndex,
  equipmentOptions,
  equipmentStatus,
  equipmentMessage,
  subcomponents,
  subcomponentStatus,
  subcomponentMessage,
  parts,
  partStatus,
  partMessage,
  errors,
  onLineChange,
  onLoadSubcomponents,
  onLoadParts,
}) => (
  <>
    <div className='col-md-4'>
      <label className='form-label required'>Equipment</label>
      <select
        aria-label={`Direct PO Spare Equipment ${lineIndex + 1}`}
        className={`form-select ${errors[`line-${lineIndex}-equipment`] ? 'is-invalid' : ''}`}
        value={line.equipmentId}
        disabled={!vesselId || equipmentStatus === 'loading'}
        onChange={(e) => {
          const selected = equipmentOptions.find((row) => String(row.id) === e.target.value)
          onLineChange(lineIndex, {
            equipmentId: e.target.value,
            equipmentName: selected?.name || '',
            componentId: '',
            componentName: '',
            partId: '',
            partName: '',
            unitOfMeasurement: '',
          })
          void onLoadSubcomponents(lineIndex, e.target.value)
        }}
      >
        <option value=''>
          {!vesselId
            ? 'Select vessel first'
            : equipmentStatus === 'loading'
            ? 'Loading equipment...'
            : equipmentStatus === 'empty'
            ? 'No equipment found for selected vessel'
            : equipmentStatus === 'error'
            ? 'Unable to load equipment'
            : 'Select equipment'}
        </option>
        {equipmentOptions.map((equipment) => (
          <option key={equipment.id} value={equipment.id}>
            {equipment.name}
          </option>
        ))}
      </select>
      {equipmentMessage && equipmentStatus !== 'loaded' && (
        <div className='form-text text-muted'>{equipmentMessage}</div>
      )}
    </div>
    <div className='col-md-4'>
      <label className='form-label required'>Sub Component</label>
      <select
        aria-label={`Direct PO Spare Subcomponent ${lineIndex + 1}`}
        className={`form-select ${errors[`line-${lineIndex}-component`] ? 'is-invalid' : ''}`}
        value={line.componentId}
        disabled={!line.equipmentId || subcomponentStatus === 'loading' || subcomponents.length === 0}
        onChange={(e) => {
          const selected = subcomponents.find((row) => String(row.id) === e.target.value)
          onLineChange(lineIndex, {
            componentId: e.target.value,
            componentName: selected?.name || '',
            partId: '',
            partName: '',
            unitOfMeasurement: '',
            manualItemName: '',
          })
          void onLoadParts(lineIndex, e.target.value)
        }}
      >
        <option value=''>
          {!line.equipmentId
            ? 'Select equipment first'
            : subcomponentStatus === 'loading'
            ? 'Loading subcomponents...'
            : subcomponentStatus === 'empty'
            ? 'No sub components found for selected equipment'
            : subcomponentStatus === 'error'
            ? 'Unable to load sub components'
            : 'Select sub component'}
        </option>
        {subcomponents.map((component) => (
          <option key={component.id} value={component.id}>
            {component.name}
          </option>
        ))}
      </select>
      {subcomponentMessage && <div className='form-text text-muted'>{subcomponentMessage}</div>}
    </div>
    <div className='col-md-4'>
      <label className='form-label'>Part / Item Name</label>
      <select
        aria-label={`Direct PO Spare Part ${lineIndex + 1}`}
        className='form-select'
        value={line.partId}
        disabled={!line.componentId || partStatus === 'loading'}
        onChange={(e) => {
          const selected = parts.find((row) => String(row.id) === e.target.value)
          onLineChange(lineIndex, {
            partId: e.target.value,
            partName: selected?.name || '',
            manualItemName: selected?.name || line.manualItemName,
            unitOfMeasurement:
              selected?.uom?.code ||
              selected?.uom?.symbol ||
              selected?.unitOfMeasure ||
              line.unitOfMeasurement,
          })
        }}
      >
        <option value=''>
          {!line.componentId
            ? 'Select sub component first'
            : partStatus === 'loading'
            ? 'Loading parts...'
            : partStatus === 'empty'
            ? 'No parts found for selected sub component'
            : partStatus === 'error'
            ? 'Unable to load parts'
            : 'Select part'}
        </option>
        {parts.map((part) => (
          <option key={part.id} value={part.id}>
            {part.name}
          </option>
        ))}
      </select>
      {partMessage && <div className='form-text text-muted'>{partMessage}</div>}
    </div>
    <div className='col-md-6'>
      <label className='form-label'>Manual Description / Specification</label>
      <input
        aria-label={`Direct PO Spare Description ${lineIndex + 1}`}
        className='form-control'
        value={line.manualItemName}
        onChange={(e) => onLineChange(lineIndex, {manualItemName: e.target.value})}
      />
    </div>
  </>
)

type ServiceLineFieldsProps = {
  line: DirectPoLineDraft
  lineIndex: number
  errors: Record<string, string>
  serviceHeads: InventoryItemHead[]
  serviceSubheads: InventoryItemSubHead[]
  onLineChange: (lineIndex: number, patch: Partial<DirectPoLineDraft>) => void
  onLoadServiceSubheads: (lineIndex: number, headId: string) => Promise<void> | void
}

export const ServiceLineFields: FC<ServiceLineFieldsProps> = ({
  line,
  lineIndex,
  errors,
  serviceHeads,
  serviceSubheads,
  onLineChange,
  onLoadServiceSubheads,
}) => (
  <>
    <SearchableLookupField
      label='Head'
      value={line.serviceHeadId}
      options={serviceHeads.map((head) => ({
        id: head.id,
        label: head.name || `Head ${head.id}`,
      }))}
      onChange={(value) => {
        onLineChange(lineIndex, {
          serviceHeadId: value,
          serviceSubHeadId: '',
          manualItemName: '',
        })
        void onLoadServiceSubheads(lineIndex, value)
      }}
      placeholder='Type to search heads'
      listId={`direct-po-service-head-${lineIndex}`}
      wrapperClassName='col-md-6'
      error={errors[`line-${lineIndex}-serviceHead`]}
    />
    <SearchableLookupField
      label='Sub Head'
      required
      value={line.serviceSubHeadId}
      options={serviceSubheads
        .filter((subHead) =>
          line.serviceHeadId ? String(subHead.inventoryItemHeadId) === String(line.serviceHeadId) : true
        )
        .map((subHead) => ({
          id: subHead.id,
          label: subHead.name || `Sub Head ${subHead.id}`,
        }))}
      onChange={(value) => {
        const selected = serviceSubheads.find((subHead) => String(subHead.id) === String(value))
        onLineChange(lineIndex, {
          serviceSubHeadId: value,
          manualItemName: selected?.name || line.manualItemName,
        })
      }}
      placeholder={line.serviceHeadId ? 'Type to search sub heads' : 'Select head first'}
      listId={`direct-po-service-subhead-${lineIndex}`}
      wrapperClassName='col-md-6'
      error={errors[`line-${lineIndex}-serviceSubHead`]}
    />
    <div className='col-md-12'>
      <label className='form-label required'>Service Scope</label>
      <input
        aria-label={`Direct PO Service Scope ${lineIndex + 1}`}
        className={`form-control ${errors[`line-${lineIndex}-service`] ? 'is-invalid' : ''}`}
        value={line.manualItemName}
        onChange={(e) => onLineChange(lineIndex, {manualItemName: e.target.value})}
        placeholder='Describe the service required'
      />
    </div>
  </>
)

type CommonLineFieldsProps = {
  line: DirectPoLineDraft
  lineIndex: number
  errors: Record<string, string>
  accounts: AccountingAccount[]
  accountsLoading: boolean
  uoms: UnitOfMeasure[]
  subaccounts: SubAccount[]
  budgetSubaccounts: SubAccount[]
  onLineChange: (lineIndex: number, patch: Partial<DirectPoLineDraft>) => void
  onLoadSubaccounts: (lineIndex: number, accountCodeId: string) => Promise<void> | void
  onLoadBudgetSubaccounts: (lineIndex: number, accountCodeId: string) => Promise<void> | void
}

export const CommonLineFields: FC<CommonLineFieldsProps> = ({
  line,
  lineIndex,
  errors,
  accounts,
  accountsLoading,
  uoms,
  subaccounts,
  budgetSubaccounts,
  onLineChange,
  onLoadSubaccounts,
  onLoadBudgetSubaccounts,
}) => (
  <>
    <div className='col-md-2'>
      <label className='form-label required'>Quantity</label>
      <input
        aria-label={`Direct PO Quantity ${lineIndex + 1}`}
        className={`form-control ${errors[`line-${lineIndex}-quantity`] ? 'is-invalid' : ''}`}
        value={line.quantity}
        onChange={(e) => onLineChange(lineIndex, {quantity: e.target.value})}
      />
    </div>
    <SearchableLookupField
        label='UOM'
        value={line.unitOfMeasurement}
        displayValue={line.unitOfMeasurement}
        options={uoms.filter((uom) => Boolean(uom.active) || uom.code === line.unitOfMeasurement).map((uom) => ({
          id: uom.code,
          label: `${uom.code} - ${uom.name}${uom.symbol ? ` (${uom.symbol})` : ''}`,
        }))}
        onChange={(value) => onLineChange(lineIndex, {unitOfMeasurement: value})}
        listId={`direct-po-uom-${lineIndex}`}
        wrapperClassName='col-md-2'
        placeholder='Search UOM'
        required
        error={errors[`line-${lineIndex}-uom`]}
      />
    <div className='col-md-2'>
      <label className='form-label required'>Unit Price</label>
      <input
        aria-label={`Direct PO Unit Price ${lineIndex + 1}`}
        className={`form-control ${errors[`line-${lineIndex}-unitPrice`] ? 'is-invalid' : ''}`}
        value={line.unitPrice}
        onChange={(e) => onLineChange(lineIndex, {unitPrice: e.target.value})}
      />
    </div>
    <div className='col-md-2'>
      <label className='form-label'>Logistics Account</label>
      <select
        aria-label={`Direct PO Logistics Account ${lineIndex + 1}`}
        className='form-select'
        value={line.logisticsAccountCodeId}
        disabled={accountsLoading || accounts.length === 0}
        onChange={(e) => {
          onLineChange(lineIndex, {logisticsAccountCodeId: e.target.value, logisticsSubaccountCodeId: ''})
          void onLoadSubaccounts(lineIndex, e.target.value)
        }}
      >
        <option value=''>
          {accountsLoading ? 'Loading accounts...' : accounts.length ? 'Select account' : 'No accounts available'}
        </option>
        {accounts.map((account) => (
          <option key={account.id} value={account.id}>
            {account.accountCodes} {account.accountName || account.name || ''}
          </option>
        ))}
      </select>
    </div>
    <div className='col-md-2'>
      <label className='form-label'>Logistics Sub Account</label>
      <select
        aria-label={`Direct PO Logistics Sub Account ${lineIndex + 1}`}
        className='form-select'
        value={line.logisticsSubaccountCodeId}
        disabled={!line.logisticsAccountCodeId}
        onChange={(e) => onLineChange(lineIndex, {logisticsSubaccountCodeId: e.target.value})}
      >
        <option value=''>
          {subaccounts.length ? 'Select logistics sub account' : 'No subaccounts available'}
        </option>
        {subaccounts.map((subaccount) => (
          <option key={subaccount.id} value={subaccount.id}>
            {subaccount.subaccountCodes}
          </option>
        ))}
      </select>
    </div>
    <div className='col-md-2'>
      <label className='form-label'>Budget Account</label>
      <select className='form-select' aria-label={`Direct PO Budget Account ${lineIndex + 1}`} value={line.budgetAccountCodeId} disabled={accountsLoading || accounts.length === 0}
        onChange={(e) => {
          onLineChange(lineIndex, {budgetAccountCodeId: e.target.value, budgetSubaccountCodeId: ''})
          void onLoadBudgetSubaccounts(lineIndex, e.target.value)
        }}>
        <option value=''>
          {accountsLoading ? 'Loading accounts...' : accounts.length ? 'Select budget account' : 'No accounts available'}
        </option>
        {accounts.map((account) => <option key={account.id} value={account.id}>{account.accountCodes} {account.accountName || account.name || ''}</option>)}
      </select>
    </div>
    <div className='col-md-2'>
      <label className='form-label'>Budget Sub Account</label>
      <select className='form-select' aria-label={`Direct PO Budget Sub Account ${lineIndex + 1}`} value={line.budgetSubaccountCodeId} disabled={!line.budgetAccountCodeId}
        onChange={(e) => onLineChange(lineIndex, {budgetSubaccountCodeId: e.target.value})}>
        <option value=''>
          {budgetSubaccounts.length ? 'Select budget sub account' : 'No subaccounts available'}
        </option>
        {budgetSubaccounts.map((subaccount) => <option key={subaccount.id} value={subaccount.id}>{subaccount.subaccountCodes}</option>)}
      </select>
    </div>
    <div className='col-md-4'>
      <label className='form-label'>Line Remarks</label>
      <input
        aria-label={`Direct PO Remarks ${lineIndex + 1}`}
        className='form-control'
        value={line.notes}
        onChange={(e) => onLineChange(lineIndex, {notes: e.target.value})}
      />
    </div>
    <div className='col-md-4'>
      <label className='form-label'>Line Attachments</label>
      <input
        aria-label={`Direct PO Line Attachments ${lineIndex + 1}`}
        type='file'
        className='form-control'
        multiple
        onChange={(e) => onLineChange(lineIndex, {lineFiles: Array.from(e.target.files || [])})}
      />
    </div>
  </>
)

type LineItemsSectionProps = {
  categoryKind: DirectPoCategoryKind
  vesselId: string
  lines: DirectPoLineDraft[]
  errors: Record<string, string>
  accounts: AccountingAccount[]
  accountsLoading: boolean
  uoms: UnitOfMeasure[]
  subaccountsByLine: Record<number, SubAccount[]>
  budgetSubaccountsByLine: Record<number, SubAccount[]>
  storeOptionsByLine: Record<number, VesselInventoryProjection[]>
  subCategories: InventoryItemSubCategory[]
  equipmentOptions: Equipment[]
  equipmentStatus: string
  equipmentMessage: string | null
  subcomponentsByLine: Record<number, SubComponent[]>
  subcomponentStatusByLine: Record<number, LoadStatus>
  subcomponentMessageByLine: Record<number, string | null>
  partsByLine: Record<number, Part[]>
  partStatusByLine: Record<number, LoadStatus>
  partMessageByLine: Record<number, string | null>
  serviceHeads: InventoryItemHead[]
  serviceSubheadsByLine: Record<number, InventoryItemSubHead[]>
  onAddLine: () => void
  onRemoveLine: (lineIndex: number) => void
  onLineChange: (lineIndex: number, patch: Partial<DirectPoLineDraft>) => void
  onLoadStoreOptions: (lineIndex: number, search: string) => Promise<void> | void
  onLoadSubaccounts: (lineIndex: number, accountCodeId: string) => Promise<void> | void
  onLoadBudgetSubaccounts: (lineIndex: number, accountCodeId: string) => Promise<void> | void
  onLoadSubcomponents: (lineIndex: number, equipmentId: string) => Promise<void> | void
  onLoadParts: (lineIndex: number, subcomponentId: string) => Promise<void> | void
  onLoadServiceSubheads: (lineIndex: number, headId: string) => Promise<void> | void
}

export const LineItemsSection: FC<LineItemsSectionProps> = ({
  categoryKind,
  vesselId,
  lines,
  errors,
  accounts,
  accountsLoading,
  uoms,
  subaccountsByLine,
  budgetSubaccountsByLine,
  storeOptionsByLine,
  subCategories,
  equipmentOptions,
  equipmentStatus,
  equipmentMessage,
  subcomponentsByLine,
  subcomponentStatusByLine,
  subcomponentMessageByLine,
  partsByLine,
  partStatusByLine,
  partMessageByLine,
  serviceHeads,
  serviceSubheadsByLine,
  onAddLine,
  onRemoveLine,
  onLineChange,
  onLoadStoreOptions,
  onLoadSubaccounts,
  onLoadBudgetSubaccounts,
  onLoadSubcomponents,
  onLoadParts,
  onLoadServiceSubheads,
}) => (
  <section className='mb-6'>
    <div className='d-flex justify-content-between align-items-center mb-3'>
      <h6 className='fw-bold mb-0'>3. Line Items</h6>
      <button type='button' className='btn btn-light-primary btn-sm' onClick={onAddLine}>
        Add line
      </button>
    </div>
    {errors.lines && <div className='text-danger small mb-3'>{errors.lines}</div>}
    <div className='d-flex flex-column gap-4'>
      {lines.map((line, lineIndex) => (
        <div key={`direct-po-line-${lineIndex}`} className='border rounded p-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <strong>Line {lineIndex + 1}</strong>
            {lines.length > 1 && (
              <button
                type='button'
                className='btn btn-light-danger btn-sm'
                onClick={() => onRemoveLine(lineIndex)}
              >
                Remove
              </button>
            )}
          </div>
          <div className='row g-4'>
            {categoryKind === 'GENERAL_STORES' && (
              <GeneralStoresLineFields
                line={line}
                lineIndex={lineIndex}
                error={errors[`line-${lineIndex}-item`]}
                storeOptions={storeOptionsByLine[lineIndex] || []}
                subCategories={subCategories}
                onLineChange={onLineChange}
                onLoadStoreOptions={onLoadStoreOptions}
              />
            )}

            {categoryKind === 'SPARES' && (
              <TechnicalSpareLineFields
                vesselId={vesselId}
                line={line}
                lineIndex={lineIndex}
                equipmentOptions={equipmentOptions}
                equipmentStatus={equipmentStatus}
                equipmentMessage={equipmentMessage}
                subcomponents={subcomponentsByLine[lineIndex] || []}
                subcomponentStatus={subcomponentStatusByLine[lineIndex] || 'idle'}
                subcomponentMessage={subcomponentMessageByLine[lineIndex] || null}
                parts={partsByLine[lineIndex] || []}
                partStatus={partStatusByLine[lineIndex] || 'idle'}
                partMessage={partMessageByLine[lineIndex] || null}
                errors={errors}
                onLineChange={onLineChange}
                onLoadSubcomponents={onLoadSubcomponents}
                onLoadParts={onLoadParts}
              />
            )}

            {categoryKind === 'SERVICES' && (
              <ServiceLineFields
                line={line}
                lineIndex={lineIndex}
                errors={errors}
                serviceHeads={serviceHeads}
                serviceSubheads={serviceSubheadsByLine[lineIndex] || []}
                onLineChange={onLineChange}
                onLoadServiceSubheads={onLoadServiceSubheads}
              />
            )}

            <CommonLineFields
              line={line}
              lineIndex={lineIndex}
              errors={errors}
              accounts={accounts}
              accountsLoading={accountsLoading}
              uoms={uoms}
              subaccounts={subaccountsByLine[lineIndex] || []}
              budgetSubaccounts={budgetSubaccountsByLine[lineIndex] || []}
              onLineChange={onLineChange}
              onLoadSubaccounts={onLoadSubaccounts}
              onLoadBudgetSubaccounts={onLoadBudgetSubaccounts}
            />
          </div>
        </div>
      ))}
    </div>
  </section>
)

type CommercialSectionProps = {
  form: DirectPoFormDraft
  currencies: CurrencyOption[]
  currencyConversionRelevant: boolean
  errors: Record<string, string>
  onFormChange: (patch: Partial<DirectPoFormDraft>) => void
}

export const CommercialSection: FC<CommercialSectionProps> = ({
  form,
  currencies,
  currencyConversionRelevant,
  errors,
  onFormChange,
}) => (
  <details open className='mb-6'>
    <summary className='fw-bold mb-3' style={{cursor: 'pointer'}}>
      4. Commercial Details
    </summary>
    <div className='row g-4 mt-1'>
      <div className='col-md-3'>
        <label className='form-label'>Quotation Currency</label>
        <SearchableLookupField
          label='Quotation Currency'
          value={form.quotationCurrency}
          options={currencies.map((currency) => ({
            id: currency.code,
            label: `${currency.code} - ${currency.displayName}`,
          }))}
          onChange={(value) => onFormChange({quotationCurrency: value})}
          listId='direct-po-quotation-currency'
          wrapperClassName='w-100'
          placeholder='Search currency'
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Company Currency</label>
        <SearchableLookupField
          label='Company Currency'
          value={form.companyCurrencyCode}
          options={currencies.map((currency) => ({
            id: currency.code,
            label: `${currency.code} - ${currency.displayName}`,
          }))}
          onChange={() => undefined}
          listId='direct-po-company-currency'
          wrapperClassName='w-100'
          placeholder='Search currency'
          disabled
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Discount</label>
        <input
          aria-label='Direct PO Discount'
          className='form-control'
          value={form.totalDiscount}
          onChange={(e) => onFormChange({totalDiscount: e.target.value})}
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Tax</label>
        <input
          aria-label='Direct PO Tax'
          className='form-control'
          value={form.vatOnLogistics}
          onChange={(e) => onFormChange({vatOnLogistics: e.target.value})}
        />
      </div>
      {currencyConversionRelevant && (
        <>
          <div className='col-md-3'>
            <label className='form-label'>Exchange Rate</label>
            <input
              aria-label='Direct PO Exchange Rate'
              className={`form-control ${errors.exchangeRate ? 'is-invalid' : ''}`}
              value={
                form.exchangeRate
                  ? formatExchangeRateDisplay(form.quotationCurrency, form.exchangeRate, form.companyCurrencyCode)
                  : 'Not configured'
              }
              readOnly
            />
            {errors.exchangeRate && <div className='invalid-feedback'>{errors.exchangeRate}</div>}
            {!form.exchangeRate && (
              <div className='form-text'>
                No exchange rate configured for {form.quotationCurrency || 'the document currency'} to{' '}
                {form.companyCurrencyCode || 'the company currency'}.
              </div>
            )}
          </div>
          <div className='col-md-3'>
            <label className='form-label'>Exchange Rate Date</label>
            <input
              aria-label='Direct PO Exchange Rate Date'
              type='date'
              className='form-control'
              value={form.exchangeRateDate}
              readOnly
            />
          </div>
          <div className='col-md-3 d-flex align-items-end'>
            <div className='form-check'>
              <input
                id='direct-po-exchange-rate-direct'
                type='checkbox'
                className='form-check-input'
                checked={form.exchangeRateDirect}
                disabled
              />
              <label htmlFor='direct-po-exchange-rate-direct' className='form-check-label'>
                Exchange rate direct
              </label>
            </div>
          </div>
        </>
      )}
      <div className='col-md-3'>
        <label className='form-label'>Freight</label>
        <input
          aria-label='Direct PO Freight'
          className='form-control'
          value={form.freightCost}
          onChange={(e) => onFormChange({freightCost: e.target.value})}
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Packaging</label>
        <input
          aria-label='Direct PO Packaging'
          className='form-control'
          value={form.packagingCost}
          onChange={(e) => onFormChange({packagingCost: e.target.value})}
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Truck</label>
        <input
          aria-label='Direct PO Truck'
          className='form-control'
          value={form.truckCost}
          onChange={(e) => onFormChange({truckCost: e.target.value})}
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Barge</label>
        <input
          aria-label='Direct PO Barge'
          className='form-control'
          value={form.bargeCost}
          onChange={(e) => onFormChange({bargeCost: e.target.value})}
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Other Logistics</label>
        <input
          aria-label='Direct PO Other Logistics'
          className='form-control'
          value={form.otherCost}
          onChange={(e) => onFormChange({otherCost: e.target.value})}
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Payment Terms</label>
        <input
          aria-label='Direct PO Payment Terms'
          className='form-control'
          value={form.paymentTerms}
          onChange={(e) => onFormChange({paymentTerms: e.target.value})}
        />
      </div>
      <div className='col-md-3'>
        <label className='form-label'>Delivery Terms</label>
        <input
          aria-label='Direct PO Delivery Terms'
          className='form-control'
          value={form.deliveryTerms}
          onChange={(e) => onFormChange({deliveryTerms: e.target.value})}
        />
      </div>
      <div className='col-md-12'>
        <label className='form-label'>Terms / Remarks</label>
        <textarea
          aria-label='Direct PO Terms'
          className='form-control'
          rows={2}
          value={form.terms}
          onChange={(e) => onFormChange({terms: e.target.value})}
        />
      </div>
    </div>
  </details>
)

type ApprovalSectionProps = {
  ownerApprovalRequired: boolean
  ownerApprovalRemark: string
  onFormChange: (patch: Partial<DirectPoFormDraft>) => void
}

export const ApprovalSection: FC = () => null

type AttachmentSectionProps = {
  filesByStage: Array<[string, File[], Dispatch<SetStateAction<File[]>>]>
}

export const AttachmentSection: FC<AttachmentSectionProps> = ({filesByStage}) => (
  <section className='mb-2'>
    <h6 className='fw-bold mb-3'>6. Attachments</h6>
    <div className='row g-4'>
      {filesByStage.map(([label, files, setter]) => (
        <div className='col-md-6' key={label}>
          <label className='form-label'>{label}</label>
          <input
            aria-label={label}
            type='file'
            className='form-control'
            multiple
            onChange={(e) => setter(Array.from(e.target.files || []))}
          />
          {files.length > 0 && (
            <div className='small text-muted mt-2'>{files.map((file) => file.name).join(', ')}</div>
          )}
        </div>
      ))}
    </div>
  </section>
)
